import { colorUtils } from "@app/common";
import { IPropertyTheme } from "@app/property/types";

import { appConfig } from "..";
import { ITheme } from "../components/ThemeProvider";

const IBE_FALLBACK_COLOR = "#0085FF";

const IBE_GLOBAL_COLOR = (window as any)?.[appConfig.globalVendorNamespace]?.[appConfig.globalAppNamespace]?.primaryColor;

/**
 * constructUnifiedThemeConfig
 * Since we have theme config from multiple sources in multiple formats, this function
 * will construct a unified theme config object that will be used in the app
 */
export const constructUnifiedThemeConfig = (propertyTheme?: IPropertyTheme, themeConfig?: ITheme): ITheme => {
    const fallbackColor = propertyTheme?.colors.primary || IBE_GLOBAL_COLOR || IBE_FALLBACK_COLOR;
    const nextThemeConfig = {
        ...(themeConfig || {}),
        key: themeConfig?.key || "light",
    };

    if (propertyTheme) {
        if (propertyTheme?.border_radius) {
            nextThemeConfig.borderRadius = getCustomBorderRadiusFromTheme(propertyTheme);
        }

        if (propertyTheme?.colors) {
            nextThemeConfig.colors = {
                primary: propertyTheme?.colors.primary || fallbackColor,
            };
        }
    }

    return nextThemeConfig;
};

export interface IApplyThemeToHtmlElementProps {
    targetElement: HTMLElement;
    theme: ITheme;
}

export const applyThemeToHtmlElement = ({ targetElement, theme }: IApplyThemeToHtmlElementProps): boolean => {
    const primaryColor = theme?.colors?.primary;
    const hslColor = primaryColor ? colorUtils.convertHexToHsl(primaryColor) : null;
    const borderRadius = theme?.borderRadius;

    if (!targetElement) {
        return false;
    }

    let hasBeenApplied = true;

    if (hslColor !== null) {
        const colorString = `${hslColor.h}, ${hslColor.s}, ${hslColor.l}`;
        const colorArray = colorString.split(",");

        colorArray[2] = " 92%";

        targetElement.style.setProperty("--hc-primary", colorString);
        targetElement.style.setProperty("--hc-secondary", colorArray.join(","));
        targetElement.style.setProperty("--hc-secondary-foreground", colorString);

        hasBeenApplied = hasBeenApplied && true;
    }

    if (borderRadius) {
        targetElement.style.setProperty("--hc-border-radius-none", borderRadius.none);
        targetElement.style.setProperty("--hc-border-radius-sm", borderRadius.sm);
        targetElement.style.setProperty("--hc-border-radius-default", borderRadius.default);
        targetElement.style.setProperty("--hc-border-radius-md", borderRadius.md);
        targetElement.style.setProperty("--hc-border-radius-lg", borderRadius.lg);
        targetElement.style.setProperty("--hc-border-radius-xl", borderRadius.xl);
        targetElement.style.setProperty("--hc-border-radius-2xl", borderRadius["2xl"]);
        targetElement.style.setProperty("--hc-border-radius-3xl", borderRadius["3xl"]);
        targetElement.style.setProperty("--hc-border-radius-full", borderRadius.full);

        hasBeenApplied = hasBeenApplied && true;
    }

    return hasBeenApplied;
};

export const getCustomBorderRadiusFromTheme = (propertyTheme: IPropertyTheme): IPropertyTheme["border_radius"] => ({
    none: "0px",
    sm: "0.125rem",
    default: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
    ...(propertyTheme?.border_radius || {}),
});
